import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import { NodeProps } from '@xyflow/react';
import { BaseNode, createInputHandle, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { getNodeCreditCost } from '@/lib/workflows/executor';
import { ImageModelType, NodeExecutionStatus } from 'types';
import {
  getDimensionsForModel,
  getModelDisplayName,
  modelsWithDescription,
} from '@/constants/imageModels';

import useLocale from 'next-translate/useTranslation';
import Button from '@/components/buttons/Button';
import { TipTapPromptEditor } from '../TipTapPromptEditor';
import { PlayIcon, RefreshIcon } from '@heroicons/react/outline';
import clsxm from '@/lib/clsxm';
import { NodeActionsDropdown } from './NodeActionsDropdown';
import EditModal from '@/components/images/EditModal';

import { SimpleTooltip } from '@/components/ui/tooltip';

// Helper function to download image
const downloadImage = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
  }
};

// Pull available image models from modelsWithDescription to stay in sync
const IMAGE_MODELS: ImageModelType[] = modelsWithDescription.map(
  (m) => m.title as ImageModelType,
);

interface ImageGenerationConfig {
  model: ImageModelType;
  dimensions: string;
  prompt: string;
  outputs: number;
}

interface ImageGenerationNodeProps extends NodeProps {
  onRunNode?: (nodeId: string) => void;
  nodeId?: string;
  readOnly?: boolean;
}

export function ImageGenerationNode({
  id,
  data,
  selected,
  onRunNode,
  nodeId,
  readOnly,
}: ImageGenerationNodeProps) {
  const { t } = useLocale('common');
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const addNode = useWorkflowStore((state) => state.addNode);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const allEdges = useWorkflowStore((state) => state.edges);

  // State to control settings visibility
  const [showSettings, setShowSettings] = useState(!data.result);
  const [showEditModal, setShowEditModal] = useState(false);

  const executionStatus =
    (data.status as NodeExecutionStatus | undefined) || 'idle';
  const isRunning = executionStatus === 'running';

  // Track previous result to detect when it changes
  const prevResultRef = useRef(data.result);

  // Auto-collapse settings when result is generated (only when result changes)
  useEffect(() => {
    const resultChanged = prevResultRef.current !== data.result;
    if (resultChanged && data.result && showSettings && !isRunning) {
      setShowSettings(false);
    }
    prevResultRef.current = data.result;
  }, [data.result, isRunning, showSettings]);

  // Auto-expand settings when running
  useEffect(() => {
    if (isRunning && !showSettings) {
      setShowSettings(true);
    }
  }, [isRunning, showSettings]);

  // Hide result if running
  const shouldShowResult = !!data.result && !isRunning;

  // Extract image URL for dropdown
  const imageUrl = useMemo(() => {
    if (typeof data.result === 'string') return data.result;
    if (Array.isArray(data.result) && data.result.length > 0) {
      return data.result[0] as string;
    }
    return undefined;
  }, [data.result]);

  const config: ImageGenerationConfig = {
    model: (data.config as ImageGenerationConfig)?.model || 'Nano Banana Pro',
    dimensions: (data.config as ImageGenerationConfig)?.dimensions || '1:1',
    prompt: (data.config as ImageGenerationConfig)?.prompt || '',
    outputs: (data.config as ImageGenerationConfig)?.outputs || 1,
  };

  const isMidjourneyModel =
    config.model === 'Midjourney V6' || config.model === 'Midjourney V7';

  // Ensure Midjourney always uses 4 outputs
  useEffect(() => {
    if (isMidjourneyModel && config.outputs !== 4) {
      updateNodeData(id, {
        config: {
          ...config,
          outputs: 4,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMidjourneyModel, config.outputs]);

  // Get valid dimensions for the current model
  const availableDimensions = useMemo(() => {
    return getDimensionsForModel({
      model: config.model,
      selectedModels: [],
    });
  }, [config.model]);

  // Reset dimensions if current selection is invalid for the model
  useEffect(() => {
    if (
      availableDimensions.length > 0 &&
      !availableDimensions.includes(config.dimensions)
    ) {
      updateNodeData(id, {
        config: {
          ...config,
          dimensions: availableDimensions[0],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.model, availableDimensions]);

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = e.target.value as ImageModelType;
    const newDimensions = getDimensionsForModel({
      model: newModel,
      selectedModels: [],
    });

    updateNodeData(id, {
      config: {
        ...config,
        model: newModel,
        // Reset to first available dimension for new model
        dimensions: newDimensions[0],
      },
    });
  };

  const handleDimensionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateNodeData(id, {
      config: {
        ...config,
        dimensions: e.target.value,
      },
    });
  };

  const handlePromptChange = (value: string) => {
    updateNodeData(id, {
      config: {
        ...config,
        prompt: value,
      },
    });
  };

  const handleOutputsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value) || 1;
    updateNodeData(id, {
      config: {
        ...config,
        outputs: value,
      },
    });
  };

  // Connected text nodes for mention autocomplete
  const getConnectedTextNodes = useCallback(() => {
    const connectedEdges = allEdges.filter(
      (edge) =>
        edge.target === id &&
        (edge.targetHandle === 'prompt' || edge.targetHandle === 'text'),
    );

    return connectedEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source);
        const baseLabel = (sourceNode?.data?.label as string) || 'Node';
        return {
          id: sourceNode?.id || '',
          label: baseLabel,
        };
      })
      .filter((n) => n.id);
  }, [id, allNodes, allEdges]);

  // Get model icon from constants
  const getModelIcon = useMemo(() => {
    const modelData = modelsWithDescription.find(
      (m) => m.title === config.model,
    );

    if (modelData?.icon) {
      const IconComponent = modelData.icon;
      return <IconComponent className='w-4 h-4' />;
    }

    // Fallback to default icon if model not found
    return (
      <svg
        className='w-4 h-4 text-purple-600'
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        viewBox='0 0 24 24'
        stroke='currentColor'
      >
        <path d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
      </svg>
    );
  }, [config.model]);

  // Calculate credit cost for this node
  const creditCost = useMemo(() => {
    const mockNode = {
      id,
      type: 'image-generation',
      position: { x: 0, y: 0 },
      data: { config },
    };
    return getNodeCreditCost(mockNode);
  }, [id, config.model, config.outputs, config.dimensions]);

  // When multiple images are generated, create separate select-image nodes
  // so each image can be used independently in the workflow.
  useEffect(() => {
    if (!Array.isArray(data.result) || data.result.length <= 1) {
      return;
    }

    const imageUrls = (data.result as unknown[]).filter(
      (url): url is string => typeof url === 'string',
    );

    if (imageUrls.length <= 1) return;

    const parentNode = allNodes.find((n) => n.id === id);
    if (!parentNode) return;

    const existingChildren = allNodes.filter((n) => {
      const nodeData = n.data as any;
      return (
        n.type === 'select-image' &&
        nodeData?.sourceNodeId === id &&
        typeof nodeData?.resultIndex === 'number'
      );
    });

    const childrenByIndex = new Map<
      number,
      (typeof existingChildren)[number]
    >();
    existingChildren.forEach((node) => {
      const idx = (node.data as any).resultIndex;
      if (typeof idx === 'number') {
        childrenByIndex.set(idx, node);
      }
    });

    imageUrls.forEach((url, index) => {
      // Keep the original node as-is; create/select nodes for additional images
      if (index === 0) return;

      const existingChild = childrenByIndex.get(index);
      if (existingChild) {
        const existingConfig = ((existingChild.data as any).config || {}) as {
          selectedImageUrl?: string;
          selectedIsVideo?: boolean;
        };

        // Skip update if already in sync to avoid infinite update loops
        if (
          existingConfig.selectedImageUrl === url &&
          existingConfig.selectedIsVideo === false
        ) {
          return;
        }

        updateNodeData(existingChild.id, {
          config: {
            ...(existingChild.data as any).config,
            selectedImageUrl: url,
            selectedIsVideo: false,
          },
          result: url,
        });
        return;
      }

      const offsetX = 320 * index;

      const newNode: any = {
        id: `select-image-${Date.now()}-${index}-${id}`,
        type: 'select-image',
        position: {
          x: parentNode.position.x + offsetX,
          y: parentNode.position.y,
        },
        data: {
          label: `Generated image ${index + 1}`,
          type: 'select-image',
          config: {
            selectedImageUrl: url,
            selectedIsVideo: false,
          },
          result: url,
          sourceNodeId: id,
          resultIndex: index,
        },
      };

      addNode(newNode);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data.result, allNodes]);

  return (
    <>
      <BaseNode
        title={(data.label as string) || t('Text to Image')}
        icon={getModelIcon}
        hasInput={false}
        hasOutput={false}
        status={executionStatus}
        selected={selected}
        nodeId={nodeId || id}
        onRunNode={onRunNode}
        readOnly={readOnly}
        customInputs={createInputHandle('text', 'prompt', 50)}
        customOutputs={createOutputHandle('image', 'image', 50)}
        disableMinWidth={!showSettings && !!data.result}
        hideToolbarActions
        customHeaderButtons={
          <>
            {(data as any)?.result && !isRunning && (
              <SimpleTooltip
                title={showSettings ? t('Hide settings') : t('Show settings')}
                side='top'
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSettings(!showSettings);
                  }}
                  className='flex-shrink-0 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors'
                >
                  <svg
                    className='w-5 h-5 text-gray-600 dark:text-gray-300'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' />
                    <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  </svg>
                </button>
              </SimpleTooltip>
            )}
            <NodeActionsDropdown
              nodeId={nodeId || id}
              nodeTitle={(data.label as string) || t('Text to Image')}
              mediaUrl={imageUrl}
              mediaType='image'
              filename='generated-image.png'
              readOnly={readOnly}
              onEditImage={imageUrl ? () => setShowEditModal(true) : undefined}
            />
          </>
        }
      >
        <div className={showSettings || !data.result ? 'w-[400px]' : ''}>
          {/* Collapsible Settings Section */}
          <div
            className={`space-y-3 px-4 pt-3 pb-3 border-b border-gray-200 dark:border-gray-700 ${
              showSettings ? '' : 'hidden'
            }`}
          >
            {/* Model Selection */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Model')}
              </label>
              <select
                value={config.model}
                onChange={handleModelChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                disabled={readOnly}
              >
                {IMAGE_MODELS.map((model) => (
                  <option key={model} value={model}>
                    {getModelDisplayName(model)}
                  </option>
                ))}
              </select>
            </div>

            {/* Dimensions */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Size')}
              </label>
              <select
                value={config.dimensions}
                onChange={handleDimensionsChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                disabled={readOnly}
              >
                {availableDimensions.map((dim) => (
                  <option key={dim} value={dim}>
                    {dim}
                  </option>
                ))}
              </select>
            </div>

            {/* Prompt */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Prompt')}
              </label>
              <TipTapPromptEditor
                value={config.prompt || ''}
                onChange={readOnly ? () => {} : handlePromptChange}
                connectedNodes={getConnectedTextNodes()}
                placeholder={t('Enter prompt...')}
              />
              <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                {t('Type @ to reference connected nodes')}
              </p>
            </div>

            {/* Number of Images */}
            <div>
              <label className='block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1'>
                {t('Number of images')}
              </label>
              <select
                value={config.outputs}
                onChange={handleOutputsChange}
                className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:border-white/5 dark:bg-slate-700/50 dark:text-white'
                disabled={readOnly}
              >
                {(isMidjourneyModel ? [4] : [1, 2, 3, 4]).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            {/* Run Button (inside settings) */}
            <div className='pt-1'>
              <Button
                className='w-full justify-center'
                variant='outline'
                isLoading={isRunning}
                onClick={(e) => {
                  e.stopPropagation();
                  onRunNode?.(nodeId || id);
                }}
              >
                {data.result ? (
                  <>
                    <RefreshIcon className='w-4 h-4 mr-2' />
                    <span>
                      {t('Regenerate')} ({creditCost}{' '}
                      {creditCost === 1 ? t('credit') : t('credits')})
                    </span>
                  </>
                ) : (
                  <>
                    <PlayIcon className='w-4 h-4 mr-2' />
                    <span>
                      {t('Run')} ({creditCost}{' '}
                      {creditCost === 1 ? t('credit') : t('credits')})
                    </span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Result Preview - show only first image */}
          {shouldShowResult && (
            <div
              className={clsxm(
                showSettings
                  ? 'w-full px-4 pb-3'
                  : ['16:9', '21:9'].includes(config.dimensions)
                    ? 'w-[700px]'
                    : 'w-[420px]',
              )}
            >
              {(() => {
                const singleUrl =
                  typeof data.result === 'string'
                    ? (data.result as string)
                    : Array.isArray(data.result) && data.result.length > 0
                      ? (data.result[0] as string)
                      : null;

                if (!singleUrl) return null;

                return (
                  <div className='relative group rounded-md overflow-hidden'>
                    <img
                      src={singleUrl}
                      alt='Generated'
                      className='w-full h-auto object-cover'
                    />
                    <div className='absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                      <button
                        onClick={() => window.open(singleUrl, '_blank')}
                        className='p-1.5 bg-white rounded-md shadow-lg hover:bg-gray-50 transition-colors'
                        title={t('Open in new tab')}
                      >
                        <svg
                          className='w-4 h-4 text-gray-700'
                          fill='none'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Error Display */}
          {!!data.error && (
            <div className='mx-3 mb-3 border-t border-red-200 bg-red-50 px-2 py-2 rounded'>
              <p className='text-xs text-red-600'>{String(data.error)}</p>
            </div>
          )}
        </div>
      </BaseNode>
      {imageUrl && (
        <EditModal
          image={{ image_url: imageUrl }}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onUpdate={(updatedImage) => {
            if (updatedImage.image_url) {
              updateNodeData(id, { result: updatedImage.image_url });
            }
          }}
        />
      )}
    </>
  );
}
